import React from "react";
import { AccountingMigrationExternalSystem } from "@common/types/domains/accounting/accountingMigration";
import { IntegrationConnection } from "@common/types/integrationConnection";
import { StandardModalHeader } from "@design-system/Modal/Modal";
import { Button } from "@design-system/Button/Button";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import { ExternalSystemGrid } from "@components/accountingMigration/source";
import { QuickbooksConnectionPicker } from "./QuickbooksConnectionPicker";
import styles from "./AccountingMigrationModal.module.scss";

interface MigrationSourceStepProps {
    entityName: string | null | undefined;
    onSelect: (system: AccountingMigrationExternalSystem | null) => void;
    onBack: () => void;
    isConnecting: boolean;
    isLoadingQuickbooksConnections: boolean;
    hasQuickbooksConnectionsError: boolean;
    showQuickbooksPicker: boolean;
    quickbooksConnections: IntegrationConnection[];
    selectedQuickbooksConnection: IntegrationConnection | null;
    onSelectQuickbooksConnection: (connection: IntegrationConnection) => void;
    onUseAnotherSource: () => void;
    onContinueQuickbooks: () => void;
    onConnectQuickbooks: () => void;
    onReconnectQuickbooks: () => void;
    onRetryQuickbooksConnections: () => void;
}

export const MigrationSourceStep: React.FC<MigrationSourceStepProps> = ({
    entityName,
    onSelect,
    onBack,
    isConnecting,
    isLoadingQuickbooksConnections,
    hasQuickbooksConnectionsError,
    showQuickbooksPicker,
    quickbooksConnections,
    selectedQuickbooksConnection,
    onSelectQuickbooksConnection,
    onUseAnotherSource,
    onContinueQuickbooks,
    onConnectQuickbooks,
    onReconnectQuickbooks,
    onRetryQuickbooksConnections,
}) => (
    <>
        <StandardModalHeader>
            <VStack gap={4}>
                <span className={styles.stepLabel}>Step 2 of 3</span>
                <div>
                    Migrate
                    {entityName && ` ${entityName}`}
                </div>
            </VStack>
        </StandardModalHeader>
        {showQuickbooksPicker ? (
            <QuickbooksConnectionPicker
                connections={quickbooksConnections}
                selectedConnection={selectedQuickbooksConnection}
                isPending={isConnecting}
                isLoadingConnections={isLoadingQuickbooksConnections}
                hasConnectionsError={hasQuickbooksConnectionsError}
                onSelect={onSelectQuickbooksConnection}
                onBack={onBack}
                onUseAnotherSource={onUseAnotherSource}
                onContinue={onContinueQuickbooks}
                onConnect={onConnectQuickbooks}
                onReconnect={onReconnectQuickbooks}
                onRetry={onRetryQuickbooksConnections}
            />
        ) : (
            <VStack gap={24}>
                <VStack gap={16}>
                    <div className={styles.description}>
                        Choose the accounting software to migrate from
                    </div>
                    <ExternalSystemGrid onSelect={onSelect} showAllSystems />
                </VStack>
                <HStack gap={8} justify="end">
                    <Button size="md" variant="secondary" onClick={onBack}>
                        Back
                    </Button>
                </HStack>
            </VStack>
        )}
    </>
);
